/**
 * The status logs of an order: one bar per log with the day, the month, the
 * user, the status and the e-mails sent, then the timezone note.
 */
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { ORDER_TYPES, STATUS_COLORS } from './order-types';
import { formatLogTime, utcOffsetLabel, type StatusLog } from './details-logic';

export function StatusLogs({ logs }: Readonly<{ logs: readonly StatusLog[] }>) {
  return (
    <>
      {logs.map((log, index) => (
        <StatusLogRow key={`${log.createdAt ?? ''}-${index}`} log={log} />
      ))}
      <div className="text-right gmt-note">
        <i>* (GMT {utcOffsetLabel()})</i>
      </div>
    </>
  );
}

function StatusLogRow({ log }: Readonly<{ log: StatusLog }>) {
  const { t } = useTranslation();
  const statusId = log.currentStatusId ?? 0;
  const confirmationEmail =
    statusId === 8 && !!log.emailSent?.orderConfirmation;
  const pickupEmail = statusId === 5 && !!log.emailSent?.orderPickup;
  return (
    <div
      className="modal-left-bar status-log mb-2"
      style={{ borderColor: STATUS_COLORS[statusId] }}
      data-testid="order-details-status-log-row"
      data-value={statusId}
    >
      <div className="status-log-day">
        <b style={{ fontSize: 30 }}>{formatLogTime(log.createdAt, 'D')}</b>
        <div className="status-log-month">
          {formatLogTime(log.createdAt, 'MMMM')}
        </div>
      </div>
      <div className="status-log-row py-2">
        <div style={{ fontSize: 14 }}>
          <span data-testid="order-details-status-log-user">
            {log.username || 'Unknown'}
          </span>
          <span
            className="float-right text-muted"
            data-testid="order-details-status-log-time"
          >
            {formatLogTime(log.createdAt, 'h:mm a')}*
          </span>
        </div>
        <div style={{ fontSize: 20 }}>
          <strong data-testid="order-details-status-log-status">
            {ORDER_TYPES[statusId] || '-'}
          </strong>
        </div>
        {confirmationEmail && (
          <small>
            <FontAwesomeIcon className="mr-2" icon={faEnvelope} />
            {t('orders.orderConfirmationEmail')}
          </small>
        )}
        {pickupEmail && (
          <small>
            <FontAwesomeIcon className="mr-2" icon={faEnvelope} />
            {t('orders.orderPickupEmail')}
          </small>
        )}
      </div>
    </div>
  );
}
